import {useEsgMatricesLookup} from '../processes/getEsgMatricesLookup'
import {useEsgMatricesSummaryLookup} from '../processes/getEsgMatricesLookup'
import {useTranslation} from 'react-i18next'
import CardBody from "../components/Dashboard/Cards/CardBody";
import Card from "../components/Dashboard/Cards/Card";
import CardHeader from "../components/Dashboard/Cards/CardHeader";
import CardTitle from "../components/Dashboard/Cards/CardTitle";
import BoxItem from "../components/Dashboard/Cards/BoxItem";
import React, {useState, useMemo, useEffect} from "react";
import SimpleBarChart from "../components/Dashboard/SimpleBarChart";
import Swal from 'sweetalert2'
import { 
  useSettingEsgMatricesTemplate, 
  useEsgMatrixUpsert, 
  useEsgMatrixDelete 
} from '../processes/SettingEsgMatrices'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';

function SortableCard({ id, children, isOverlay = false }) {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({ 
        id,
        transition: null, 
    });
    
    const style = {
        opacity: isDragging && !isOverlay ? 0.3 : 1,
        cursor: 'grab',
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        transform: 'none',
        transition: 'opacity 0.2s',
    };
    
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
}

const replaceVariablePlaceholder = (text, variableValue) => {
  if (!text) return text
  if (!variableValue) return text
  return text.replace(
    /{variable}/g,
    `<span style="background-color: #ffc107; padding: 2px 6px; border-radius: 3px; font-weight: 600;">${variableValue}</span>`
  )
}

const hasVariablePlaceholder = (text) => {
  return text?.includes('{variable}') || false
}

function SettingsPopup({ section, templateItems, onClose, onSaveSuccess }) {
    const [items, setItems] = useState([])
    const [savingRows, setSavingRows] = useState({})
    const [deletingRows, setDeletingRows] = useState({})
    const upsertMutation = useEsgMatrixUpsert()
    const deleteMutation = useEsgMatrixDelete()

    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `.swal-high-z-index { z-index: 99999 !important; }`
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])

    useEffect(() => {
        if (templateItems) {
            const transformedItems = templateItems.map(item => ({
                ...item,
                sequence: item.sequence ?? '',
                variable: item.variable ?? '',
            }))
            setItems(transformedItems)
        }
    }, [templateItems])

    const onInputChange = (index, field, value) => {
        setItems(prev => {
            const newItems = [...prev]
            newItems[index][field] = value
            return newItems
        })
    }

    const onSaveRow = async (index) => {
        const item = items[index]
        if (!item.key || item.key.trim() === '') {
            Swal.fire({ html: `<p>Key is required</p>`, icon: 'error' })
            return
        }

        const hasVariable = hasVariablePlaceholder(item.contentEn) || hasVariablePlaceholder(item.contentId)
        if (hasVariable && (!item.variable || item.variable.trim() === '')) {
            Swal.fire({ html: `<p>Variable is required</p>`, icon: 'error' })
            return
        }

        if (!item.id) {
            if (!item.sequence || item.sequence === '') {
                Swal.fire({ html: `<p>Sequence is required for new items</p>`, icon: 'error' })
                return
            }
            if (!item.variable || item.variable.trim() === '') {
                Swal.fire({ html: `<p>Variable is required for new items</p>`, icon: 'error' })
                return
            }
        }

        const rowKey = `${section}-${index}`
        setSavingRows(prev => ({ ...prev, [rowKey]: true }))

        try {
            const payload = {
                sequence: item.sequence ? parseInt(item.sequence, 10) : 0,
                key: item.key,
                variable: item.variable || '',
            }

            await upsertMutation.mutateAsync(payload)
            onClose()
            Swal.fire({ html: `<p>Data saved successfully!</p>`, icon: 'success', timer: 1500, showConfirmButton: false })
            onSaveSuccess()
        } catch (error) {
            Swal.fire({
                html: `<p>${error.response?.data?.message || error.message || 'Failed to save data'}</p>`,
                icon: 'error',
            })
        } finally {
            setSavingRows(prev => {
                const copy = { ...prev }
                delete copy[rowKey]
                return copy
            })
        }
    }

    const onDeleteRow = async (index, id) => {
        if (!id) {
            await Swal.fire({
                html: `<p>Cannot delete item without ID. Please save it first.</p>`,
                icon: 'info',
                customClass: { container: 'swal-high-z-index' }
            })
            return
        }

        const result = await Swal.fire({
            title: 'Confirm Delete',
            html: `<p>Are you sure you want to delete this item?</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            customClass: { container: 'swal-high-z-index' },
            backdrop: true,
            allowOutsideClick: true,
            allowEscapeKey: true,
        })

        if (!result.isConfirmed) return

        const rowKey = `${section}-${index}`
        setDeletingRows(prev => ({ ...prev, [rowKey]: true }))

        try {
            await deleteMutation.mutateAsync({ id })
            onClose()
            Swal.fire({ 
                html: `<p>Data deleted successfully!</p>`, 
                icon: 'success', 
                timer: 1500,
                showConfirmButton: false,
                customClass: { container: 'swal-high-z-index' }
            })
            onSaveSuccess()
        } catch (error) {
            Swal.fire({
                html: `<p>${error.response?.data?.message || error.message || 'Failed to delete data'}</p>`,
                icon: 'error',
                customClass: { container: 'swal-high-z-index' }
            })
        } finally {
            setDeletingRows(prev => {
                const copy = { ...prev }
                delete copy[rowKey]
                return copy
            })
        }
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '20px',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    width: '100%',
                    maxWidth: '1200px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        padding: '20px 24px',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div>
                        <h5 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                            Edit {section.charAt(0).toUpperCase() + section.slice(1)} Template
                        </h5>
                        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                            {items.length} items
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        ×
                    </button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                    {items.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-bordered align-middle mb-0" style={{ fontSize: '14px' }}>
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '100px' }}>Sequence</th>
                                        <th>Content EN</th>
                                        <th>Content ID</th>
                                        <th style={{ width: '150px' }}>Variable</th>
                                        <th style={{ width: '180px' }}>Updated At</th>
                                        <th style={{ width: '120px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => {
                                        const rowKey = `${section}-${index}`
                                        const isSaving = savingRows[rowKey]
                                        const isDeleting = deletingRows[rowKey]
                                        const hasVariable = hasVariablePlaceholder(item.contentEn) || hasVariablePlaceholder(item.contentId)
                                        const variableValue = String(item.variable || '').trim()
                                        const hasValidVariable = !hasVariable || variableValue.length > 0
                                        const hasValidKey = !!item.key?.trim()
                                        const hasValidSequence = item.sequence !== '' && item.sequence !== null && item.sequence !== undefined
                                        let canSave = false
                                        if (!item.id) canSave = hasValidKey && hasValidSequence && variableValue.length > 0
                                        else canSave = hasValidKey && hasValidVariable
                                        const canDelete = Boolean(item.id)
                                        const displayContentEn = replaceVariablePlaceholder(item.contentEn, item.variable)
                                        const displayContentId = replaceVariablePlaceholder(item.contentId, item.variable)
                                        return (
                                            <tr key={rowKey}>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={item.sequence ?? ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value
                                                            if (/^\d*$/.test(value)) onInputChange(index, 'sequence', value)
                                                        }}
                                                        placeholder="-"
                                                        disabled={isSaving || isDeleting}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="text-start" style={{ fontSize: '13px' }} dangerouslySetInnerHTML={{ __html: displayContentEn || '-' }} />
                                                </td>
                                                <td>
                                                    <div className="text-start" style={{ fontSize: '13px' }} dangerouslySetInnerHTML={{ __html: displayContentId || '-' }} />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={item.variable || ''}
                                                        onChange={(e) => onInputChange(index, 'variable', e.target.value)}
                                                        placeholder={hasVariable ? 'Enter value' : '-'}
                                                        disabled={isSaving || isDeleting || !hasVariable}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        style={{ fontSize: '12px' }}
                                                        value={
                                                            item.updatedAt
                                                                ? new Date(item.updatedAt).toLocaleString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                    second: '2-digit',
                                                                }).replace(',', '')
                                                                : '-'
                                                        }
                                                        readOnly
                                                    />
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <button
                                                            onClick={() => onSaveRow(index)}
                                                            className="btn btn-sm btn-success d-flex align-items-center justify-content-center"
                                                            style={{ width: '36px', height: '36px' }}
                                                            title="Save"
                                                            disabled={isSaving || isDeleting || !canSave}
                                                        >
                                                            {isSaving ? <span className="spinner-border spinner-border-sm" /> : <i className="ri-save-line fs-5"></i>}
                                                        </button>
                                                        <button
                                                            onClick={() => onDeleteRow(index, item.id)}
                                                            className="btn btn-sm btn-danger d-flex align-items-center justify-content-center"
                                                            style={{ width: '36px', height: '36px' }}
                                                            title="Delete"
                                                            disabled={isSaving || isDeleting || !canDelete}
                                                        >
                                                            {isDeleting ? <span className="spinner-border spinner-border-sm" /> : <i className="ri-delete-bin-5-line fs-5"></i>}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#9ca3af' }}>
                            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No template data available</p>
                            <p style={{ fontSize: '14px' }}>Template items will appear here once configured</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default function ClientEsgMatrices() {
    const {data: esgData, isLoading, isError, error, refetch: refetchEsg} = useEsgMatricesLookup()
    const {data: summaryData, isLoading: isSummaryLoading} = useEsgMatricesSummaryLookup()
    const {data: templateData, isLoading: isTemplateLoading, isFetching: isTemplateFetching, refetch: refetchTemplate} = useSettingEsgMatricesTemplate()
    const {i18n} = useTranslation()
    const [selectedYear, setSelectedYear] = useState('2025')
    const [yearOptions, setYearOptions] = useState([{year:'2025'}])
    const [allItemsOrder, setAllItemsOrder] = useState([])
    const [activePopup, setActivePopup] = useState(null)
    const [activeId, setActiveId] = useState(null)
    const [hiddenCharts, setHiddenCharts] = useState([])
    const [isLoadingStorage, setIsLoadingStorage] = useState(true)

    const handleSaveSuccess = async () => {
        try {
            await Promise.all([refetchEsg(), refetchTemplate()])
        } catch {}
    }

    // Load hidden charts from storage
    useEffect(() => {
        const loadHiddenCharts = async () => {
            try {
                const result = await window.storage.get('esg-hidden-charts')
                if (result && result.value) {
                    setHiddenCharts(JSON.parse(result.value))
                }
            } catch (error) {
                console.log('No hidden charts found or error loading:', error)
            } finally {
                setIsLoadingStorage(false)
            }
        }
        loadHiddenCharts()
    }, [])

    // Save hidden charts to storage
    const saveHiddenCharts = async (charts) => {
        try {
            await window.storage.set('esg-hidden-charts', JSON.stringify(charts))
        } catch (error) {
            console.error('Error saving hidden charts:', error)
        }
    }

    const handleRemoveChart = async (chartId) => {
        const result = await Swal.fire({
            title: 'Remove Chart',
            html: `<p>Are you sure you want to remove this chart?</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Remove',
            cancelButtonText: 'Cancel',
        })

        if (result.isConfirmed) {
            const newHiddenCharts = [...hiddenCharts, chartId]
            setHiddenCharts(newHiddenCharts)
            await saveHiddenCharts(newHiddenCharts)
            
            // Remove from display order
            setAllItemsOrder(prev => prev.filter(id => id !== chartId))
            
            Swal.fire({
                html: `<p>Chart removed successfully!</p>`,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            })
        }
    }

    const handleRestoreAllCharts = async () => {
        const result = await Swal.fire({
            title: 'Restore All Charts',
            html: `<p>Do you want to restore all hidden charts?</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Restore',
            cancelButtonText: 'Cancel',
        })

        if (result.isConfirmed) {
            setHiddenCharts([])
            await saveHiddenCharts([])
            
            Swal.fire({
                html: `<p>All charts restored!</p>`,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            })
        }
    }

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    useEffect(() => {
        document.body.style.overflowX = 'hidden'
        document.documentElement.style.overflowX = 'hidden'
        return () => {
            document.body.style.overflowX = ''
            document.documentElement.style.overflowX = ''
        }
    }, [])

    const onChangeYear = (event) => {
        setSelectedYear(Number(event.target.value))
    }

    const getRandomImage = (seed) => `https://picsum.photos/seed/${seed}/400/250`

    const getLocalizedContent = (item) => {
        const lang = i18n.language
        if (lang === 'en') return item.contentEn || item.contentId || 'No content available'
        return item.contentId || item.contentEn || 'Tidak ada konten tersedia'
    }

    const groupedSummaryData = useMemo(() => {
        if (!summaryData?.data) return {}
        const currentYear = new Date().getFullYear()
        const last3Years = [currentYear - 2, currentYear - 1, currentYear]
        const grouped = {}
        summaryData.data.forEach(item => {
            if (!grouped[item.key]) grouped[item.key] = { data: [], unit: item.unit || '' }
            grouped[item.key].data.push({ year: item.year, value: item.amount, hasData: true, label: null })
        })
        Object.keys(grouped).forEach(key => {
            const existingYears = grouped[key].data.map(d => d.year)
            last3Years.forEach(year => {
                if (!existingYears.includes(year)) {
                    grouped[key].data.push({ year, value: 0, hasData: false, label: 'No Data' })
                }
            })
            grouped[key].data.sort((a, b) => a.year - b.year)
        })
        return grouped
    }, [summaryData])

    useEffect(() => {
        const cardKeys = ['environment', 'social', 'governance', 'economics'].map(key => `card-${key}`)
        const chartKeys = Object.keys(groupedSummaryData)
            .map(key => `chart-${key}`)
            .filter(chartId => !hiddenCharts.includes(chartId)) 
        const combined = [...cardKeys, ...chartKeys]
        if (combined.length > 0) setAllItemsOrder(combined)
    }, [groupedSummaryData, hiddenCharts])

    const handleDragStart = (event) => {
        setActiveId(event.active.id)
    }

    const handleDragEnd = (event) => {
        const {active, over} = event
        
        setActiveId(null)
        
        if (over && active.id !== over.id) {
            setAllItemsOrder((items) => {
                const oldIndex = items.indexOf(active.id)
                const newIndex = items.indexOf(over.id)
                
                const newItems = [...items]
                const temp = newItems[oldIndex]
                newItems[oldIndex] = newItems[newIndex]
                newItems[newIndex] = temp
                
                return newItems
            })
        }
    }

    const handleDragCancel = () => {
        setActiveId(null)
    }

    if (isLoading || isSummaryLoading || isTemplateLoading || isLoadingStorage)
        return <div className="d-flex justify-content-center align-items-center vh-100 bg-light">Loading ESG Matrices...</div>

    if (isError)
        return <div className="d-flex justify-content-center align-items-center vh-100 bg-light text-danger">Error: {error?.message || 'Unknown error'}</div>

    const sectionColors = {
        environment: '#16a34a',
        social: '#2563eb',
        governance: '#ca8a04',
        economics: '#9333ea',
    }

    const sectionDisplayNames = {
        environment: 'Environment',
        social: 'Social',
        governance: 'Governance',
        economics: 'Economics',
    }

    const renderItem = (itemId, index) => {
        if (itemId.startsWith('card-')) {
            const section = itemId.replace('card-', '');
            const items = esgData?.[section] || [];
            return (
                <SortableCard key={itemId} id={itemId}>
                    <Card>
                        <CardHeader>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <CardTitle>{sectionDisplayNames[section] || section}</CardTitle>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setActivePopup(section);
                                    }}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px 8px',
                                        fontSize: '20px',
                                        color: '#6b7280',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '4px',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    title="Edit template"
                                >
                                    ⋮
                                </button>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <img
                                src={getRandomImage(section)}
                                alt={section}
                                style={{
                                    width: '100%',
                                    maxWidth: '100%',
                                    height: 180,
                                    objectFit: 'cover',
                                    borderTopLeftRadius: 12,
                                    borderTopRightRadius: 12,
                                    display: 'block'
                                }}
                            />
                            <BoxItem
                                borderColor={sectionColors[section] || '#0ea5e9'}
                                corner={null}
                                value={
                                    <div style={{ display: 'block', width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                                        {items.length > 0 ? (
                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                {items.map((item) => (
                                                    <li key={item.key} style={{ paddingBottom: '0.5rem', width: '100%', maxWidth: '100%' }}>
                                                        <div style={{ fontSize: 15, color: '#111827', lineHeight: 1.5, wordBreak: 'break-word', overflowWrap: 'break-word', width: '100%', maxWidth: '100%' }}>
                                                            {getLocalizedContent(item)}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                                                No data available. Click ⋮ to edit template.
                                            </div>
                                        )}
                                    </div>
                                }
                            />
                        </CardBody>
                    </Card>
                </SortableCard>
            )
        } else if (itemId.startsWith('chart-')) {
            const key = itemId.replace('chart-', '');
            const chartData = groupedSummaryData[key];
            if (!chartData) return null;
            const title = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            return (
                <SortableCard key={itemId} id={itemId}>
                    <div style={{ position: 'relative' }}>
                        <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveChart(itemId);
                        }}
                        style={{
                            position: 'absolute',
                            top: '5px',
                            right: '50px',
                            zIndex: 10,
                            background: 'transparent',
                            border: 'none',
                            color: '#6b7280', // abu-abu netral
                            fontSize: '30px',
                            cursor: 'pointer',
                            padding: '2px 6px',
                            borderRadius: '6px',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f3f4f6' // sedikit highlight saat hover
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                        title="Remove chart"
                        >
                        ×
                        </button>

                        <SimpleBarChart
                            data={chartData.data}
                            unitLabel={chartData.unit}
                            title={title}
                            seriesName={title}
                            colorIndex={index}
                            showYearLabels={true}
                            noDataLabel="No Data"
                        />
                    </div>
                </SortableCard>
            );
        }
        return null;
    };

    return (
        <>
            <div 
                className='d-flex flex-column gap-3' 
                style={{ padding: '94px 0 84px', width: '100%', maxWidth: '100%', overflowX: 'hidden', margin: 0 }}
            >
                <div 
                    className="align-items-center d-flex justify-content-between mb-4"
                    style={{ paddingLeft: '12px', paddingRight: '12px', maxWidth: '100%' }}
                >
                    <h4 className="mb-sm-0" style={{color: '#09564B', fontSize: 'clamp(1rem, 4vw, 1.5rem)'}}>
                        ESG MATRIX
                    </h4>
                    <div className="d-flex align-items-center gap-2">
                        {hiddenCharts.length > 0 && (
                            <button
                                onClick={handleRestoreAllCharts}
                                className="btn btn-sm btn-outline-success"
                                style={{ fontSize: '13px', padding: '6px 12px' }}
                                title={`Restore ${hiddenCharts.length} hidden chart(s)`}
                            >
                                <i className="ri-refresh-line me-1"></i>
                                Restore Charts ({hiddenCharts.length})
                            </button>
                        )}
                        {selectedYear && (
                            <select
                                id="selectedEmissionYear"
                                className="form-select"
                                style={{width: 'auto', minWidth: '80px'}}
                                value={selectedYear}
                                onChange={onChangeYear}
                            >
                                {yearOptions.map((yearOpt) => (
                                    <option key={yearOpt.year} value={yearOpt.year}>
                                        {yearOpt.year}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
                <DndContext 
                    sensors={sensors} 
                    collisionDetection={closestCenter} 
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <SortableContext items={allItemsOrder} strategy={rectSortingStrategy}>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
                                gap: '1rem',
                                padding: '0 12px',
                                backgroundColor: '#f9fafb',
                                width: '100%',
                                maxWidth: '1600px', 
                                margin: '0 auto', 
                                boxSizing: 'border-box',
                            }}
                        >
                            {allItemsOrder.map((itemId, index) => renderItem(itemId, index))}
                        </div>
                    </SortableContext>
                    <DragOverlay>
                        {activeId ? (
                            <div style={{ 
                                opacity: 0.8, 
                                transform: 'rotate(3deg) scale(1.05)',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                                cursor: 'grabbing'
                            }}>
                                {renderItem(activeId, allItemsOrder.indexOf(activeId))}
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </div>
            {activePopup && !isTemplateFetching && (
                <SettingsPopup
                    section={activePopup}
                    templateItems={templateData?.[activePopup] || []}
                    onClose={() => setActivePopup(null)}
                    onSaveSuccess={handleSaveSuccess}
                />
            )}
        </>
    )
}